import { formatOeDisplay, formatJpWithAlias } from '@/utils/partDisplay';
import React, { useState, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { 
  FileSpreadsheet, Upload, Download, Check, X, AlertCircle, 
  Search, User, Building2, Loader2, FileText, Package, Plus
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import * as XLSX from 'xlsx';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ExcelImportQuotation = ({ 
  isOpen, 
  onClose, 
  onQuotationCreated,
  isAdmin = false  // Admin mode allows customer selection
}) => {
  const { token, user } = useAuth();
  const fileInputRef = useRef(null);
  
  // State
  const [step, setStep] = useState(1); // 1: Upload, 2: Preview, 3: Result
  const [loading, setLoading] = useState(false);
  const [fileName, setFileName] = useState('');
  const [parsedData, setParsedData] = useState([]);
  
  // Admin: Customer selection
  const [customerSearch, setCustomerSearch] = useState('');
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [searchingCustomers, setSearchingCustomers] = useState(false);
  
  // Preview/Result data
  const [previewData, setPreviewData] = useState(null);
  const [savedQuotation, setSavedQuotation] = useState(null);
  
  // Admin price edits (jp_part_no -> price)
  const [customPrices, setCustomPrices] = useState({});
  // Extra charges (settable on the preview step before saving)
  const [packingCharge, setPackingCharge] = useState(0);
  const [courierCharge, setCourierCharge] = useState(0);
  const [addPartSearch, setAddPartSearch] = useState('');
  const [addPartResults, setAddPartResults] = useState([]);
  // Inline replace flow for unmatched rows. We track the row currently
  // being replaced ({part_no, quantity}) plus its own search state so it
  // doesn't collide with the global "Add more items" search above.
  const [replacingFor, setReplacingFor] = useState(null);
  const [replaceSearch, setReplaceSearch] = useState('');
  const [replaceResults, setReplaceResults] = useState([]);
  const [replaceLoading, setReplaceLoading] = useState(false);
  const [addPartLoading, setAddPartLoading] = useState(false);
  const [addPartImages, setAddPartImages] = useState({});
  const [zoomImage, setZoomImage] = useState(null);
  const addPartTimer = React.useRef(null);

  // Which mode the admin/customer is using: "quotation" or "order"
  const [mode, setMode] = useState('quotation');

  // Search customers (admin only)
  const searchCustomers = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setCustomers([]);
      return;
    }
    
    setSearchingCustomers(true);
    try {
      const response = await axios.get(`${API}/admin/customers/search?q=${encodeURIComponent(query)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data.customers || []);
    } catch (error) {
      console.error('Error searching customers:', error);
      setCustomers([]);
    } finally {
      setSearchingCustomers(false);
    }
  }, [token]);

  // Handle file selection
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    // Validate file type
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/csv'
    ];
    
    if (!validTypes.includes(file.type) && !file.name.match(/\.(xlsx|xls|csv)$/i)) {
      toast.error('Please upload an Excel file (.xlsx, .xls) or CSV file');
      return;
    }
    
    setFileName(file.name);
    parseExcelFile(file);
  };

  // Parse Excel file
  const parseExcelFile = (file) => {
    setLoading(true);
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        
        // Get first sheet
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        
        // Convert to JSON
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        
        if (jsonData.length < 2) {
          toast.error('Excel file is empty or has no data rows');
          setLoading(false);
          return;
        }
        
        // Find column indices
        const headers = jsonData[0].map(h => String(h || '').toLowerCase().trim());
        
        // Auto-detect part number column
        const partNoIndex = headers.findIndex(h => 
          h.includes('part') || h.includes('number') || h.includes('no') || 
          h.includes('oe') || h.includes('jp') || h === 'partnumber' || h === 'partno'
        );
        
        // Auto-detect quantity column
        const qtyIndex = headers.findIndex(h => 
          h.includes('qty') || h.includes('quantity') || h.includes('pcs') || 
          h.includes('pieces') || h === 'q'
        );
        
        // Auto-detect price column (for admin handshake pricing)
        const priceIndex = headers.findIndex(h => 
          h.includes('price') || h.includes('rate') || h.includes('amount') || 
          h.includes('cost') || h === 'mrp'
        );
        
        if (partNoIndex === -1) {
          // If no header found, assume first column is part number
          toast.info('No header found. Using first column as Part Numbers.');
        }
        
        // Parse data rows
        const parts = [];
        const actualPartNoIndex = partNoIndex === -1 ? 0 : partNoIndex;
        
        for (let i = 1; i < jsonData.length; i++) {
          const row = jsonData[i];
          const partNo = String(row[actualPartNoIndex] || '').trim();
          
          if (partNo) {
            let quantity = 1;
            if (qtyIndex !== -1 && row[qtyIndex]) {
              quantity = parseInt(row[qtyIndex]) || 1;
            }
            
            let price = null;
            if (priceIndex !== -1 && row[priceIndex]) {
              price = parseFloat(row[priceIndex]) || null;
            }
            
            parts.push({
              part_no: partNo,
              quantity: quantity,
              ...(price !== null && { price })
            });
          }
        }
        
        if (parts.length === 0) {
          toast.error('No valid part numbers found in the file');
          setLoading(false);
          return;
        }
        
        setParsedData(parts);
        toast.success(`Found ${parts.length} part numbers in the file`);
        
        // For customers, directly go to preview
        // For admins, need to select customer first
        if (!isAdmin) {
          processImport(parts, false);
        }
      } catch (error) {
        console.error('Error parsing Excel:', error);
        toast.error('Failed to parse Excel file. Please check the format.');
      } finally {
        setLoading(false);
      }
    };
    
    reader.onerror = () => {
      toast.error('Failed to read file');
      setLoading(false);
    };
    
    reader.readAsArrayBuffer(file);
  };

  // Process import (preview or save)
  const processImport = async (parts, save = false) => {
    setLoading(true);
    try {
      const payload = {
        part_numbers: parts || parsedData,
        save_quotation: save,
        // When in the "Pending Order" tab AND we're saving, ask the backend
        // to also write an `orders` document. The quotation is still created
        // (it's the canonical source) and immediately marked converted —
        // this keeps audit/lineage symmetric with the Convert-Quote flow.
        save_as_order: save && mode === 'order',
        packing_charge: parseFloat(packingCharge) || 0,
        courier_charge: parseFloat(courierCharge) || 0,
      };
      
      // Admin can specify customer
      if (isAdmin && selectedCustomer) {
        payload.customer_id = selectedCustomer.customer_id;
      }
      
      // When saving, send admin-set custom prices
      if (save && isAdmin && Object.keys(customPrices).length > 0) {
        payload.custom_prices = customPrices;
      }
      
      const response = await axios.post(`${API}/quotations/import-excel/process`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPreviewData(response.data);
      
      // Initialize customPrices from Excel prices + existing data on preview
      if (!save && response.data.matched_items) {
        const initialPrices = {};
        response.data.matched_items.forEach(item => {
          if (item.is_admin_set_price || item.price !== item.mrp) {
            // Keep prices that were set via Excel or are already special
          }
        });
        // Import prices from Excel column if present
        (parts || parsedData || []).forEach(p => {
          if (p.price && p.price > 0) {
            const matched = response.data.matched_items?.find(m => 
              m.searched_part_no === p.part_no || m.jp_part_no === p.part_no.toUpperCase()
            );
            if (matched) {
              initialPrices[matched.jp_part_no] = p.price;
            }
          }
        });
        if (Object.keys(initialPrices).length > 0) {
          setCustomPrices(initialPrices);
        }
      }
      
      if (save && response.data.quotation_saved) {
        setSavedQuotation(response.data);
        setStep(3);
        const savedOrder = response.data.order_saved && response.data.order_id;
        const priceMsg = response.data.handshake_prices_saved
          ? ` (${response.data.handshake_prices_saved} handshake prices saved)`
          : '';
        if (savedOrder) {
          toast.success(`Pending order ${response.data.order_id} created!${priceMsg}`);
        } else {
          toast.success(`Quotation ${response.data.quotation_id} created!${priceMsg}`);
        }
        if (onQuotationCreated) {
          onQuotationCreated(response.data);
        }
      } else {
        setStep(2);
      }
    } catch (error) {
      console.error('Error processing import:', error);
      toast.error(error.response?.data?.detail || 'Failed to process import');
    } finally {
      setLoading(false);
    }
  };

  // Download unavailable parts as Excel
  const downloadUnavailableParts = () => {
    if (!previewData?.unmatched_parts?.length) return;
    
    const worksheet = XLSX.utils.json_to_sheet(
      previewData.unmatched_parts.map(p => ({
        'Part Number': p.part_no,
        'Quantity': p.quantity,
        'Status': 'Not Available'
      }))
    );
    
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Unavailable Parts');
    
    XLSX.writeFile(workbook, 'Unavailable_Parts.xlsx');
    toast.success('Downloaded unavailable parts list');
  };


  // Dynamic search with debounce
  React.useEffect(() => {
    if (addPartTimer.current) clearTimeout(addPartTimer.current);
    if (addPartSearch.trim().length < 2) { setAddPartResults([]); return; }
    addPartTimer.current = setTimeout(async () => {
      setAddPartLoading(true);
      try {
        const res = await axios.get(`${API}/categories/All%20Products/parts`, {
          params: {
            search: addPartSearch.trim(),
            limit: 10,
            include_inactive_brands: isAdmin ? 'true' : undefined,
          },
          headers: { Authorization: `Bearer ${token}` }
        });
        const parts = res.data?.parts || res.data || [];
        setAddPartResults(Array.isArray(parts) ? parts : []);
        // Load images
        (Array.isArray(parts) ? parts : []).forEach(p => {
          if (p.image && !addPartImages[p.jp_part_no]) {
            axios.get(`${API}/image/${p.image}`).then(r => {
              if (r.data.data_url) setAddPartImages(prev => ({ ...prev, [p.jp_part_no]: r.data.data_url }));
            }).catch(() => {});
          }
        });
      } catch { setAddPartResults([]); }
      setAddPartLoading(false);
    }, 300);
    return () => { if (addPartTimer.current) clearTimeout(addPartTimer.current); };
  }, [addPartSearch, token]);

  // Debounced search for the "Replace" picker on each unavailable row.
  const replaceTimer = useRef(null);
  React.useEffect(() => {
    if (!replacingFor) return;
    if (replaceTimer.current) clearTimeout(replaceTimer.current);
    if (replaceSearch.trim().length < 2) { setReplaceResults([]); return; }
    replaceTimer.current = setTimeout(async () => {
      setReplaceLoading(true);
      try {
        const res = await axios.get(`${API}/categories/All%20Products/parts`, {
          params: {
            search: replaceSearch.trim(),
            limit: 10,
            // Admins/staff who use this modal must be able to pick parts
            // from deactivated master categories too (parity with the
            // Edit Order / Create Order flows). The backend `_get_parts_by_category_impl`
            // honours this flag for admin & staff only — customer
            // requests pass it harmlessly and still get the active-brand
            // filter.
            include_inactive_brands: isAdmin ? 'true' : undefined,
          },
          headers: { Authorization: `Bearer ${token}` }
        });
        const parts = res.data?.parts || res.data || [];
        setReplaceResults(Array.isArray(parts) ? parts : []);
        (Array.isArray(parts) ? parts : []).forEach(p => {
          if (p.image && !addPartImages[p.jp_part_no]) {
            axios.get(`${API}/image/${p.image}`).then(r => {
              if (r.data.data_url) setAddPartImages(prev => ({ ...prev, [p.jp_part_no]: r.data.data_url }));
            }).catch(() => {});
          }
        });
      } catch { setReplaceResults([]); }
      setReplaceLoading(false);
    }, 300);
    return () => { if (replaceTimer.current) clearTimeout(replaceTimer.current); };
  }, [replaceSearch, replacingFor, token, addPartImages]);

  // Pre-seed the search with the unmatched part_no so the admin sees
  // likely matches immediately (e.g., looking up "MISC631" might surface
  // close MISC parts in the catalogue).
  const openReplaceFor = (part) => {
    setReplacingFor(part);
    setReplaceSearch(part.part_no || '');
    setReplaceResults([]);
  };

  const closeReplacePicker = () => {
    setReplacingFor(null);
    setReplaceSearch('');
    setReplaceResults([]);
  };

  const confirmReplace = (product) => {
    if (!replacingFor) return;
    addPartToList(product, {
      quantity: replacingFor.quantity,
      removeUnmatchedKey: `${replacingFor.part_no}|${replacingFor.quantity}`,
      replacingPartNo: replacingFor.part_no,
    });
    closeReplacePicker();
  };

  const addPartToList = (product, opts = {}) => {
    const jpPartNo = product.jp_part_no || '';
    const exists = previewData.matched_items.some(i => i.jp_part_no === jpPartNo);
    if (exists) { toast.error(`${jpPartNo} already in list`); return; }
    const customerDisc = selectedCustomer?.discount_percentage || 0;
    const mrp = product.mrp || 0;
    const price = mrp * (1 - customerDisc / 100);
    const qty = Math.max(1, parseInt(opts.quantity, 10) || 1);
    const newItem = {
      jp_part_no: jpPartNo,
      part_name: product.part_name || '',
      oe_part_no: product.oe_part_no || '',
      mrp, price,
      quantity: qty,
      total: price * qty,
      subtotal: price * qty,
      has_special_price: false, category: product.category || '',
    };
    setPreviewData(prev => {
      const updated = { ...prev };
      updated.matched_items = [...updated.matched_items, newItem];
      // If we're replacing a specific unavailable row, drop it from
      // unmatched_parts so the list size shrinks visibly.
      if (opts.removeUnmatchedKey) {
        updated.unmatched_parts = (updated.unmatched_parts || [])
          .filter(u => `${u.part_no}|${u.quantity}` !== opts.removeUnmatchedKey);
      }
      updated.total_amount = updated.matched_items.reduce((s, i) => s + ((customPrices[i.jp_part_no] !== undefined ? customPrices[i.jp_part_no] : i.price) * (i.quantity || 1)), 0);
      return updated;
    });
    if (opts.replacingPartNo) {
      toast.success(`Replaced ${opts.replacingPartNo} with ${jpPartNo}`);
    } else {
      toast.success(`Added ${jpPartNo} - ${product.part_name}`);
    }
    setAddPartSearch('');
    setAddPartResults([]);
  };


  // Reset form
  const resetForm = () => {
    setStep(1);
    setMode('quotation');
    setFileName('');
    setParsedData([]);
    setPreviewData(null);
    setSavedQuotation(null);
    setSelectedCustomer(null);
    setCustomerSearch('');
    setCustomers([]);
    setPackingCharge(0);
    setCourierCharge(0);
    setCustomPrices({});
    setReplacingFor(null);
    setReplaceSearch('');
    setReplaceResults([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Calculate basic price (pre-GST)
  const calculateBasicPrice = (price) => (price || 0) / 1.18;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <FileSpreadsheet className="w-6 h-6 text-green-600" />
            Import Excel — {mode === 'order' ? 'Create Pending Order' : 'Create Quotation'}
          </DialogTitle>
        </DialogHeader>

        {/* Mode tabs — only at step 1. Once preview is open, mode is locked
            so the matching pipeline and saved record stay consistent. */}
        {step === 1 && (
          <div className="flex gap-1 border-b -mb-2" data-testid="excel-import-mode-tabs">
            <button
              type="button"
              onClick={() => setMode('quotation')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${mode === 'quotation' ? 'border-green-600 text-green-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
              data-testid="excel-mode-quotation"
            >
              <FileText className="w-4 h-4 inline mr-1.5" />
              Create Quotation
            </button>
            <button
              type="button"
              onClick={() => setMode('order')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${mode === 'order' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
              data-testid="excel-mode-order"
            >
              <Package className="w-4 h-4 inline mr-1.5" />
              Create Pending Order
            </button>
          </div>
        )}

        {/* Step 1: Upload */}
        {step === 1 && (
          <div className="space-y-6">
            {/* Admin: Customer Selection */}
            {isAdmin && (
              <Card className="border-blue-200 bg-blue-50">
                <CardHeader className="py-3">
                  <CardTitle className="text-sm font-medium text-blue-800">
                    Step 1: Select Customer for {mode === 'order' ? 'Pending Order' : 'Quotation'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {selectedCustomer ? (
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{selectedCustomer.name}</p>
                        {selectedCustomer.company_name && (
                          <p className="text-sm text-gray-600">{selectedCustomer.company_name}</p>
                        )}
                        <p className="text-xs text-gray-500">{selectedCustomer.mobile}</p>
                        {selectedCustomer.discount_percentage > 0 && (
                          <Badge className="mt-1 bg-green-100 text-green-700">
                            {selectedCustomer.discount_percentage}% Discount
                          </Badge>
                        )}
                      </div>
                      <Button variant="outline" size="sm" onClick={() => setSelectedCustomer(null)}>
                        Change
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                          placeholder="Search customer by name or company..."
                          value={customerSearch}
                          onChange={(e) => {
                            setCustomerSearch(e.target.value);
                            searchCustomers(e.target.value);
                          }}
                          className="pl-10"
                        />
                      </div>
                      
                      {searchingCustomers && (
                        <div className="text-center py-2 text-gray-500">Searching...</div>
                      )}
                      
                      {customers.length > 0 && (
                        <div className="border rounded-lg max-h-48 overflow-y-auto">
                          {customers.map((c) => (
                            <div
                              key={c.mobile}
                              onClick={() => {
                                setSelectedCustomer(c);
                                setCustomerSearch('');
                                setCustomers([]);
                              }}
                              className="p-3 border-b last:border-b-0 hover:bg-gray-50 cursor-pointer"
                            >
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="font-medium">{c.name}</p>
                                  {c.company_name && (
                                    <p className="text-sm text-gray-600">{c.company_name}</p>
                                  )}
                                </div>
                                {c.discount_percentage > 0 && (
                                  <Badge variant="outline" className="text-xs">
                                    {c.discount_percentage}% off
                                  </Badge>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* File Upload */}
            <Card className={isAdmin && !selectedCustomer ? 'opacity-50 pointer-events-none' : ''}>
              <CardHeader className="py-3">
                <CardTitle className="text-sm font-medium">
                  {isAdmin ? 'Step 2: Upload Excel File' : 'Upload Excel File'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div 
                  className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-green-500 transition-colors cursor-pointer"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  
                  {loading ? (
                    <div className="flex flex-col items-center gap-3">
                      <Loader2 className="w-12 h-12 text-green-600 animate-spin" />
                      <p className="text-gray-600">Processing file...</p>
                    </div>
                  ) : fileName ? (
                    <div className="flex flex-col items-center gap-3">
                      <FileSpreadsheet className="w-12 h-12 text-green-600" />
                      <p className="font-medium text-gray-900">{fileName}</p>
                      <p className="text-sm text-gray-500">{parsedData.length} part numbers found</p>
                      <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); resetForm(); }}>
                        Choose Different File
                      </Button>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-3">
                      <Upload className="w-12 h-12 text-gray-400" />
                      <div>
                        <p className="font-medium text-gray-700">Click to upload Excel file</p>
                        <p className="text-sm text-gray-500 mt-1">
                          Supported: .xlsx, .xls, .csv
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Instructions */}
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-medium text-gray-700 mb-2">Excel Format:</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Column: <strong>Part Number</strong> or <strong>Part No</strong></li>
                    <li>• Supports <strong>JP Part Numbers</strong>, <strong>OE Part Numbers</strong>, or <strong>mixed lists</strong></li>
                    <li>• Optional Column: <strong>Qty</strong> or <strong>Quantity</strong> (default: 1)</li>
                    <li>• First row should be headers</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Process Button for Admin */}
            {isAdmin && parsedData.length > 0 && selectedCustomer && (
              <div className="flex justify-end">
                <Button 
                  onClick={() => processImport(parsedData, false)} 
                  disabled={loading}
                  className={mode === 'order' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-600 hover:bg-green-700'}
                  data-testid="excel-preview-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                  Preview {mode === 'order' ? 'Order' : 'Quotation'}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Preview */}
        {step === 2 && previewData && (
          <div className="space-y-4">
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="bg-green-50 border-green-200">
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold text-green-700">{previewData.matched_items?.length || 0}</div>
                  <div className="text-sm text-green-600">Parts Found</div>
                </CardContent>
              </Card>
              <Card className="bg-red-50 border-red-200">
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold text-red-700">{previewData.unmatched_parts?.length || 0}</div>
                  <div className="text-sm text-red-600">Not Found</div>
                </CardContent>
              </Card>
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold text-blue-700">{previewData.discount_percentage || 0}%</div>
                  <div className="text-sm text-blue-600">Discount</div>
                </CardContent>
              </Card>
              <Card className="bg-amber-50 border-amber-200">
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold text-amber-700">₹{(previewData.total_amount || 0).toLocaleString('en-IN')}</div>
                  <div className="text-sm text-amber-600">Total (incl. GST)</div>
                </CardContent>
              </Card>
            </div>

            {/* Customer Info */}
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <User className="w-5 h-5 text-gray-500" />
                  <div>
                    <p className="font-medium">{previewData.customer_name}</p>
                    {previewData.company_name && (
                      <p className="text-sm text-gray-600">{previewData.company_name}</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Matched Items */}
            {previewData.matched_items?.length > 0 && (
              <Card>
                <CardHeader className="py-3 bg-green-50">
                  <CardTitle className="text-sm font-medium text-green-800 flex items-center gap-2">
                    <Check className="w-4 h-4" />
                    Available Parts ({previewData.matched_items.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="max-h-60 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-100 sticky top-0">
                        <tr>
                          <th className="p-2 text-left">Searched</th>
                          <th className="p-2 text-left">JP Part No</th>
                          <th className="p-2 text-left">Part Name</th>
                          <th className="p-2 text-right">Qty</th>
                          <th className="p-2 text-right">{isAdmin ? 'Set Price' : 'Price (Pre-GST)'}</th>
                          <th className="p-2 text-right">Amount</th>
                          <th className="p-2 w-10"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {previewData.matched_items.map((item, idx) => {
                          const adminPrice = customPrices[item.jp_part_no];
                          const displayPrice = adminPrice !== undefined ? adminPrice : item.price;
                          const displaySubtotal = displayPrice * (item.quantity || 1);
                          return (
                          <tr key={idx} className="border-b hover:bg-gray-50">
                            <td className="p-2">
                              <div className="font-mono text-xs text-gray-600">{item.searched_part_no}</div>
                              <Badge variant="outline" className="text-xs mt-1">
                                {item.matched_via || 'JP'}
                              </Badge>
                            </td>
                            <td className="p-2 font-mono text-blue-600">{formatJpWithAlias(item)}</td>
                            <td className="p-2">
                              {item.part_name}
                              {item.has_special_price && !adminPrice && (
                                <Badge className="ml-2 bg-amber-100 text-amber-700 text-xs">Handshake</Badge>
                              )}
                              {adminPrice !== undefined && (
                                <Badge className="ml-2 bg-green-100 text-green-700 text-xs">Custom</Badge>
                              )}
                            </td>
                            <td className="p-2 text-right">
                              <Input
                                type="number"
                                min="1"
                                className="w-16 h-7 text-right text-sm ml-auto [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                value={item.quantity}
                                onChange={(e) => {
                                  const newQty = parseInt(e.target.value) || 1;
                                  setPreviewData(prev => {
                                    const updated = { ...prev };
                                    const items = [...updated.matched_items];
                                    items[idx] = { ...items[idx], quantity: newQty, subtotal: (items[idx].price || 0) * newQty };
                                    updated.matched_items = items;
                                    updated.total_amount = items.reduce((s, i) => s + ((customPrices[i.jp_part_no] !== undefined ? customPrices[i.jp_part_no] : i.price) * (i.quantity || 1)), 0);
                                    return updated;
                                  });
                                }}
                                data-testid={`qty-input-${item.jp_part_no}`}
                              />
                            </td>
                            <td className="p-2 text-right">
                              {isAdmin ? (
                                <Input
                                  type="text"
                                  inputMode="decimal"
                                  className="w-24 h-7 text-right text-sm ml-auto [appearance:textfield]"
                                  placeholder={calculateBasicPrice(item.price).toFixed(0)}
                                  value={customPrices[item.jp_part_no] ?? ''}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    setCustomPrices(prev => {
                                      const next = { ...prev };
                                      if (val === '' || val === undefined) {
                                        delete next[item.jp_part_no];
                                      } else {
                                        next[item.jp_part_no] = parseFloat(val) || 0;
                                      }
                                      return next;
                                    });
                                  }}
                                  data-testid={`price-input-${item.jp_part_no}`}
                                />
                              ) : (
                                <>₹{calculateBasicPrice(item.price).toFixed(2)}</>
                              )}
                            </td>
                            <td className="p-2 text-right font-medium">
                              ₹{(adminPrice !== undefined ? displaySubtotal : calculateBasicPrice(item.subtotal)).toFixed(2)}
                            </td>
                            <td className="p-2 text-center">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 w-7 p-0 text-red-400 hover:text-red-600 hover:bg-red-50"
                                onClick={() => {
                                  setPreviewData(prev => {
                                    const updated = { ...prev };
                                    const items = updated.matched_items.filter((_, i) => i !== idx);
                                    updated.matched_items = items;
                                    updated.total_amount = items.reduce((s, i) => s + ((customPrices[i.jp_part_no] !== undefined ? customPrices[i.jp_part_no] : i.price) * (i.quantity || 1)), 0);
                                    return updated;
                                  });
                                }}
                                data-testid={`remove-item-${item.jp_part_no}`}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Add More Items */}
                  {isAdmin && (
                    <div className="p-3 border-t bg-blue-50">
                      <div className="flex gap-2 items-center">
                        <div className="relative flex-1">
                          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                          <Input
                            type="text"
                            placeholder="Search part number or name to add..."
                            className="h-8 text-sm pl-8"
                            data-testid="add-part-input"
                            value={addPartSearch}
                            onChange={(e) => setAddPartSearch(e.target.value)}
                          />
                        </div>
                        {addPartLoading && <Loader2 className="w-4 h-4 animate-spin text-blue-600" />}
                      </div>

                      {/* Search Results Dropdown */}
                      {addPartResults.length > 0 && (
                        <div className="mt-2" data-testid="add-part-results">
                          <div className="text-xs text-gray-500 mb-1.5">
                            Found {addPartResults.length} products — click to add
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-64 overflow-y-auto">
                            {addPartResults.map((product) => {
                              const imgUrl = addPartImages[product.jp_part_no];
                              const customerDisc = selectedCustomer?.discount_percentage || 0;
                              const mrp = product.mrp || 0;
                              const discPrice = mrp * (1 - customerDisc / 100);
                              const alreadyAdded = previewData?.matched_items?.some(i => i.jp_part_no === product.jp_part_no);

                              return (
                                <div
                                  key={product.jp_part_no}
                                  className={`border rounded-lg bg-white shadow-sm hover:shadow-md transition-shadow overflow-hidden ${alreadyAdded ? 'opacity-50' : ''}`}
                                  data-testid={`search-result-${product.jp_part_no}`}
                                >
                                  {/* Product Image */}
                                  <div
                                    className="h-20 bg-gray-50 flex items-center justify-center p-1 cursor-pointer"
                                    onClick={() => imgUrl && setZoomImage(imgUrl)}
                                  >
                                    {imgUrl ? (
                                      <img src={imgUrl} alt={product.part_name} className="max-h-full max-w-full object-contain" />
                                    ) : product.image ? (
                                      <div className="text-xs text-gray-400">Loading...</div>
                                    ) : (
                                      <Package className="w-6 h-6 text-gray-300" />
                                    )}
                                  </div>
                                  {/* Product Info */}
                                  <div className="p-2">
                                    <p className="text-xs font-medium text-gray-900 line-clamp-1">{product.part_name}</p>
                                    <p className="text-xs text-gray-500 font-mono mt-0.5">{formatJpWithAlias(product)}</p>
                                    {product.oe_part_no && <p className="text-xs text-gray-400 truncate">{formatOeDisplay(product.oe_part_no, true)}</p>}
                                    <div className="flex items-center justify-between mt-1.5 pt-1.5 border-t">
                                      <div>
                                        {customerDisc > 0 ? (
                                          <>
                                            <span className="text-xs text-gray-400 line-through">₹{mrp.toFixed(0)}</span>
                                            <span className="text-sm font-bold text-green-600 ml-1">₹{discPrice.toFixed(0)}</span>
                                          </>
                                        ) : (
                                          <span className="text-sm font-bold text-gray-900">₹{mrp.toFixed(0)}</span>
                                        )}
                                      </div>
                                      <Button
                                        size="sm"
                                        className="h-6 text-xs bg-blue-600 hover:bg-blue-700 text-white px-2"
                                        onClick={() => addPartToList(product)}
                                        disabled={alreadyAdded}
                                        data-testid={`add-result-${product.jp_part_no}`}
                                      >
                                        {alreadyAdded ? 'Added' : <><Plus className="w-3 h-3 mr-0.5" /> Add</>}
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* No Results Message */}
                      {addPartSearch.trim().length >= 2 && addPartResults.length === 0 && !addPartLoading && (
                        <div className="mt-2 text-xs text-gray-500 text-center py-2" data-testid="add-part-no-results">
                          No products found for "{addPartSearch}"
                        </div>
                      )}
                    </div>
                  )}

                  {/* Totals */}
                  <div className="p-4 bg-gray-50 border-t space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Total Basic Amount:</span>
                      <span>₹{calculateBasicPrice(previewData.total_amount).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>GST (18%):</span>
                      <span>₹{(previewData.total_amount - calculateBasicPrice(previewData.total_amount)).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm font-medium pt-2 border-t">
                      <span>Subtotal (with GST):</span>
                      <span>₹{(previewData.total_amount || 0).toFixed(2)}</span>
                    </div>

                    {/* Extra Charges Inputs */}
                    <div className="pt-3 border-t">
                      <Label className="text-gray-600 text-xs uppercase tracking-wide mb-2 block">Extra Charges (Optional)</Label>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label htmlFor="excel-packing-charge" className="text-xs text-gray-600">Packing Charge</Label>
                          <div className="relative mt-1">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">₹</span>
                            <Input
                              id="excel-packing-charge"
                              type="number"
                              min="0"
                              step="0.01"
                              value={packingCharge}
                              onChange={(e) => setPackingCharge(e.target.value)}
                              placeholder="0"
                              className="pl-7 h-9 text-sm"
                              data-testid="excel-packing-charge-input"
                            />
                          </div>
                        </div>
                        <div>
                          <Label htmlFor="excel-courier-charge" className="text-xs text-gray-600">Courier Charge</Label>
                          <div className="relative mt-1">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">₹</span>
                            <Input
                              id="excel-courier-charge"
                              type="number"
                              min="0"
                              step="0.01"
                              value={courierCharge}
                              onChange={(e) => setCourierCharge(e.target.value)}
                              placeholder="0"
                              className="pl-7 h-9 text-sm"
                              data-testid="excel-courier-charge-input"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {(parseFloat(packingCharge) > 0 || parseFloat(courierCharge) > 0) && (
                      <>
                        {parseFloat(packingCharge) > 0 && (
                          <div className="flex justify-between text-sm text-gray-600">
                            <span>Packing Charge</span>
                            <span>₹{parseFloat(packingCharge).toFixed(2)}</span>
                          </div>
                        )}
                        {parseFloat(courierCharge) > 0 && (
                          <div className="flex justify-between text-sm text-gray-600">
                            <span>Courier Charge</span>
                            <span>₹{parseFloat(courierCharge).toFixed(2)}</span>
                          </div>
                        )}
                      </>
                    )}

                    <div className="flex justify-between font-bold text-lg border-t pt-2">
                      <span>Grand Total:</span>
                      <span className="text-green-600" data-testid="excel-grand-total">
                        ₹{((previewData.total_amount || 0) + (parseFloat(packingCharge) || 0) + (parseFloat(courierCharge) || 0)).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Unmatched Parts */}
            {previewData.unmatched_parts?.length > 0 && (
              <Card className="border-red-200">
                <CardHeader className="py-3 bg-red-50">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-medium text-red-800 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      Unavailable Parts ({previewData.unmatched_parts.length})
                    </CardTitle>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={downloadUnavailableParts}
                      className="text-red-700 border-red-300"
                    >
                      <Download className="w-4 h-4 mr-2" /> Download List
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="max-h-60 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-red-50 sticky top-0">
                        <tr>
                          <th className="p-2 text-left">Part Number</th>
                          <th className="p-2 text-right">Qty</th>
                          <th className="p-2 text-left">Status</th>
                          <th className="p-2 text-right">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {previewData.unmatched_parts.map((part, idx) => {
                          const rowKey = `${part.part_no}|${part.quantity}`;
                          const isReplacing = replacingFor && `${replacingFor.part_no}|${replacingFor.quantity}` === rowKey;
                          return (
                            <React.Fragment key={idx}>
                              <tr className="border-b">
                                <td className="p-2 font-mono">{part.part_no}</td>
                                <td className="p-2 text-right">{part.quantity}</td>
                                <td className="p-2 text-red-600">{part.reason}</td>
                                <td className="p-2 text-right">
                                  {!isReplacing ? (
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="outline"
                                      className="h-7 text-[11px] px-2 border-red-300 text-red-700 hover:bg-red-100"
                                      onClick={() => openReplaceFor(part)}
                                      data-testid={`excel-replace-btn-${part.part_no}`}
                                    >
                                      Replace
                                    </Button>
                                  ) : (
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="ghost"
                                      className="h-7 text-[11px] px-2 text-gray-600"
                                      onClick={closeReplacePicker}
                                      data-testid={`excel-replace-cancel-${part.part_no}`}
                                    >
                                      Cancel
                                    </Button>
                                  )}
                                </td>
                              </tr>
                              {isReplacing && (
                                <tr className="bg-amber-50/60">
                                  <td colSpan={4} className="p-3 border-b border-amber-200">
                                    <div className="flex items-center gap-2 mb-2">
                                      <div className="relative flex-1">
                                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                                        <Input
                                          type="text"
                                          autoFocus
                                          placeholder={`Search a JP part to replace ${part.part_no} (qty will stay ${part.quantity})`}
                                          className="h-8 text-sm pl-8"
                                          data-testid={`excel-replace-search-${part.part_no}`}
                                          value={replaceSearch}
                                          onChange={(e) => setReplaceSearch(e.target.value)}
                                        />
                                      </div>
                                      {replaceLoading && <Loader2 className="w-4 h-4 animate-spin text-amber-600" />}
                                    </div>
                                    {replaceResults.length > 0 && (
                                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-56 overflow-y-auto">
                                        {replaceResults.map((product) => {
                                          const imgUrl = addPartImages[product.jp_part_no];
                                          const customerDisc = selectedCustomer?.discount_percentage || 0;
                                          const mrp = product.mrp || 0;
                                          const discPrice = mrp * (1 - customerDisc / 100);
                                          const alreadyAdded = previewData?.matched_items?.some(i => i.jp_part_no === product.jp_part_no);
                                          return (
                                            <div
                                              key={product.jp_part_no}
                                              className={`border rounded-lg bg-white shadow-sm hover:shadow-md transition-shadow overflow-hidden ${alreadyAdded ? 'opacity-50' : ''}`}
                                              data-testid={`replace-result-${product.jp_part_no}`}
                                            >
                                              <div className="h-20 bg-gray-50 flex items-center justify-center p-1">
                                                {imgUrl ? (
                                                  <img src={imgUrl} alt={product.part_name} className="max-h-full max-w-full object-contain" />
                                                ) : (
                                                  <Package className="w-6 h-6 text-gray-300" />
                                                )}
                                              </div>
                                              <div className="p-2">
                                                <p className="text-xs font-medium text-gray-900 line-clamp-1">{product.part_name}</p>
                                                <p className="text-xs text-gray-500 font-mono mt-0.5">{formatJpWithAlias(product)}</p>
                                                {product.oe_part_no && <p className="text-xs text-gray-400 truncate">{formatOeDisplay(product.oe_part_no, true)}</p>}
                                                <div className="flex items-center justify-between mt-1.5 pt-1.5 border-t">
                                                  <div>
                                                    {customerDisc > 0 ? (
                                                      <>
                                                        <span className="text-xs text-gray-400 line-through">₹{mrp.toFixed(0)}</span>
                                                        <span className="text-sm font-bold text-green-600 ml-1">₹{discPrice.toFixed(0)}</span>
                                                      </>
                                                    ) : (
                                                      <span className="text-sm font-bold text-gray-900">₹{mrp.toFixed(0)}</span>
                                                    )}
                                                  </div>
                                                  <Button
                                                    size="sm"
                                                    className="h-6 text-xs bg-amber-600 hover:bg-amber-700 text-white px-2"
                                                    onClick={() => confirmReplace(product)}
                                                    disabled={alreadyAdded}
                                                    data-testid={`excel-replace-pick-${product.jp_part_no}`}
                                                  >
                                                    {alreadyAdded ? 'In list' : 'Use this'}
                                                  </Button>
                                                </div>
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}
                                    {replaceSearch.trim().length >= 2 && replaceResults.length === 0 && !replaceLoading && (
                                      <div className="text-xs text-gray-500 text-center py-2">
                                        No JP parts found for "{replaceSearch}"
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Admin Custom Prices Notice */}
            {isAdmin && Object.keys(customPrices).length > 0 && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
                <strong>{Object.keys(customPrices).length}</strong> custom handshake price(s) set. These will be saved as permanent handshake prices for this customer.
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => { setStep(1); setPreviewData(null); }}>
                Back
              </Button>
              <div className="flex gap-2">
                {previewData.unmatched_parts?.length > 0 && (
                  <Button variant="outline" onClick={downloadUnavailableParts}>
                    <Download className="w-4 h-4 mr-2" /> Download Unavailable
                  </Button>
                )}
                {previewData.matched_items?.length > 0 && (
                  <Button 
                    onClick={() => processImport(parsedData, true)} 
                    disabled={loading}
                    className={mode === 'order' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-600 hover:bg-green-700'}
                    data-testid="excel-save-btn"
                  >
                    {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : (mode === 'order' ? <Package className="w-4 h-4 mr-2" /> : <FileText className="w-4 h-4 mr-2" />)}
                    {mode === 'order' ? 'Save Pending Order' : 'Save Quotation'}
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Result */}
        {step === 3 && savedQuotation && (
          <div className="space-y-6 text-center py-6">
            <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center ${savedQuotation.order_saved ? 'bg-blue-100' : 'bg-green-100'}`}>
              <Check className={`w-8 h-8 ${savedQuotation.order_saved ? 'text-blue-600' : 'text-green-600'}`} />
            </div>
            
            <div>
              <h3 className="text-xl font-bold text-gray-900">
                {savedQuotation.order_saved ? 'Pending Order Created!' : 'Quotation Created!'}
              </h3>
              <p className="text-gray-600 mt-2">
                {savedQuotation.order_saved ? (
                  <>Order <span className="font-mono font-bold text-blue-600">{savedQuotation.order_id}</span> has been saved.</>
                ) : (
                  <>Quotation <span className="font-mono font-bold text-blue-600">{savedQuotation.quotation_id}</span> has been saved.</>
                )}
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 inline-block mx-auto">
              <div className="grid grid-cols-2 gap-4 text-left">
                <div>
                  <p className="text-sm text-gray-500">Items</p>
                  <p className="font-bold">{savedQuotation.matched_items?.length || 0}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total</p>
                  <p className="font-bold text-green-600">₹{(savedQuotation.total_amount || 0).toLocaleString('en-IN')}</p>
                </div>
              </div>
            </div>

            {savedQuotation.unmatched_parts?.length > 0 && (
              <div className="flex justify-center">
                <Button variant="outline" onClick={downloadUnavailableParts}>
                  <Download className="w-4 h-4 mr-2" /> Download {savedQuotation.unmatched_parts.length} Unavailable Parts
                </Button>
              </div>
            )}

            <Button onClick={handleClose} className="bg-green-600 hover:bg-green-700">
              Done
            </Button>
          </div>
        )}
      </DialogContent>

      {/* Zoom Image Modal */}
      {zoomImage && (
        <Dialog open={!!zoomImage} onOpenChange={() => setZoomImage(null)}>
          <DialogContent className="max-w-2xl bg-white p-2">
            <img src={zoomImage} alt="Product" className="w-full h-auto max-h-[80vh] object-contain" />
          </DialogContent>
        </Dialog>
      )}
    </Dialog>
  );
};

export default ExcelImportQuotation;
